
import React, { memo, useMemo, useState, useCallback } from 'react';
import { Rect, Line, Group } from 'react-konva';
import { isToday, eachDayOfInterval, isWeekend } from 'date-fns';
import Konva from 'konva';

interface SchedulerGridProps {
  startDate: Date;
  endDate: Date;
  dayWidth: number;
  trackHeight: number;
  trackY: number;
  trackId: string;
  hoveredDayIndex?: number | null;
  hoveredTrackId?: string | null;
  onCellHover: (trackId: string, dayIndex: number) => void;
  onCellClick: (trackId: string, date: Date) => void;
}

export const SchedulerGrid = memo(function SchedulerGrid({ 
  startDate, 
  endDate, 
  dayWidth, 
  trackHeight, 
  trackY,
  trackId,
  hoveredDayIndex = null,
  hoveredTrackId = null,
  onCellHover,
  onCellClick
}: SchedulerGridProps) {
  // Memoize days calculation
  const days = useMemo(() => eachDayOfInterval({ 
    start: startDate, 
    end: endDate 
  }), [startDate, endDate]);

  // Calculate mouse position to day index
  const handleMouseMove = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return;
    
    const mousePos = stage.getPointerPosition();
    if (!mousePos) return;
    
    // Calculate day index based on mouse X position
    const dayIndex = Math.floor(mousePos.x / dayWidth);
    
    // Ensure dayIndex is within valid range
    if (dayIndex >= 0 && dayIndex < days.length) {
      onCellHover(trackId, dayIndex);
    }
  }, [days.length, dayWidth, onCellHover, trackId]);

  const handleClick = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return;
    
    const mousePos = stage.getPointerPosition();
    if (!mousePos) return;
    
    // Calculate day index based on mouse X position
    const dayIndex = Math.floor(mousePos.x / dayWidth);
    
    // Ensure dayIndex is within valid range
    if (dayIndex >= 0 && dayIndex < days.length) {
      onCellClick(trackId, days[dayIndex]);
    }
  }, [days, dayWidth, onCellClick, trackId]);

  // Memoize highlights to reduce calculations
  const dayHighlights = useMemo(() => {
    return days.map((day, index) => {
      const isWeekendDay = isWeekend(day);
      const isTodayDay = isToday(day);

      // Only return non-white backgrounds
      if (isTodayDay || isWeekendDay) {
        let fillColor = "#FFFFFF"; // Default color
        if (isTodayDay) {
          fillColor = "#E5F6FF";
        } else if (isWeekendDay) {
          fillColor = "#F3F4F6";
        }

        return {
          index,
          fillColor,
        };
      }
      return null;
    }).filter(Boolean);
  }, [days]);

  // Current highlight for hovered cell
  const currentHighlight = useMemo(() => {
    if (hoveredDayIndex !== null && hoveredTrackId === trackId) {
      return {
        x: hoveredDayIndex * dayWidth,
        width: dayWidth,
        fill: "#e5f6ff"
      };
    }
    return null;
  }, [hoveredDayIndex, hoveredTrackId, trackId, dayWidth]);

  return (
    <Group>
      {/* Draw day highlights (weekends, today) first */}
      {dayHighlights.map(highlight => (
        <Rect
          key={`highlight-${highlight.index}`}
          x={highlight.index * dayWidth}
          y={trackY}
          width={dayWidth}
          height={trackHeight}
          fill={highlight.fillColor}
        />
      ))}

      {/* Draw hover highlight */}
      {currentHighlight && (
        <Rect
          x={currentHighlight.x}
          y={trackY}
          width={currentHighlight.width}
          height={trackHeight}
          fill={currentHighlight.fill}
        />
      )}

      {/* Draw vertical day separator lines */}
      {days.map((_, index) => {
        const x = index * dayWidth;
        return (
          <Line
            key={`vline-${index}`}
            points={[x, trackY, x, trackY + trackHeight]}
            stroke="#e5e7eb"
            strokeWidth={1}
          />
        );
      })}
      
      {/* Draw final vertical line at the end */}
      <Line
        points={[days.length * dayWidth, trackY, days.length * dayWidth, trackY + trackHeight]}
        stroke="#e5e7eb"
        strokeWidth={1}
      />
      
      {/* Draw a single clickable/hoverable area for the entire track */}
      <Rect
        x={0}
        y={trackY}
        width={days.length * dayWidth}
        height={trackHeight}
        fill="rgba(0,0,0,0)" // Transparent for click/hover
        onMouseMove={handleMouseMove}
        onClick={handleClick}
      />
    </Group>
  );
});
